import { PageController } from '@client/components/_core/layout/PageController';
import { type TabItem, Tabs } from '@client/components/_core/navigation/Tabs';
import { useMyEmails } from '@client/components/_shared/email/store';
import { UpgradeTag } from '@client/components/_ui/display/UpgradeTag';
import { useIsLiteOrg } from '@client/hooks/business/org/useIsLiteOrg';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { trpc } from '@client/trpc/client';
import { showLiteUpgradePopover } from '@client/utils/domain/lite';
import { ENABLE_EMAIL_WORKLOAD_MANAGER_FLAG } from '@shared/constants/business/communication';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Badge } from 'antd';
import { Briefcase } from 'lucide-react';
import { memo, useMemo } from 'react';

export const Route = createFileRoute('/console/$orgId/workload-manager')({
    staticData: {
        metadata: {
            title: 'Workload manager',
        },
        ui: {
            widthVariant: 'default',
            icon: Briefcase,
            title: 'Workload manager',
            subtitle: 'Your assigned work in one place',
            useTitleForChildren: true,
        },
        breadcrumb: {
            inactive: true,
        },
        roles: ['user', 'shared_edit_user', 'org_admin'],
        modules: ['claim', 'compliance', 'risk'],
    },
    component: WorkloadManager,
});

const EMAILS_BADGE_OFFSET = [15, 0] as const satisfies [number, number];

const EmailsTabLabel = memo(function EmailsTabLabel({ count }: { count: number | undefined }) {
    // Skip the Badge entirely when there's nothing to show. Mounting the Badge with
    // count=undefined and later transitioning to a number triggers antd's zoom-enter
    // animation (~300ms) on every fresh page load. Rendering the Badge only once we
    // have a positive count means it mounts already visible — motionAppear=false on
    // antd's CSSMotion skips the appear animation, so no initial-load flicker.
    if (!count) return <>Emails</>;
    return (
        <Badge count={count} offset={EMAILS_BADGE_OFFSET} size="small">
            Emails
        </Badge>
    );
});

function WorkloadManager() {
    // CUR-363: the unread badge follows the Emails tab's own "My emails" toggle so it always
    // matches the list (each tab owns its scope control — Tasks has its own "My work" switch).
    const myEmails = useMyEmails();
    const complaintsWorkloadEnabled = useFeatureFlag('enable-complaints-workload', false);
    const emailsEnabled = useFeatureFlag(ENABLE_EMAIL_WORKLOAD_MANAGER_FLAG, false);

    const queryInput = useMemo(() => ({ myEmails: myEmails === true }), [myEmails]);

    // Curium Lite (CUR-379): emails are premium — the tab stays visible with an upgrade pill and
    // opens the contact page; the 15 s unread poll is skipped (the server refuses email procedures
    // for Lite orgs, so polling would just loop on FORBIDDEN). With the email workload flag off the
    // tab is hidden altogether (the emails route redirects to Tasks), so there is nothing to poll.
    const isLiteOrg = useIsLiteOrg();

    const { data: unreadCount } = trpc.email.countUnreadEmails.useQuery(
        isLiteOrg || !emailsEnabled ? skipToken : queryInput,
        {
            refetchInterval: 1000 * 15,
        },
    );

    const items = useMemo<TabItem[]>(
        () => [
            {
                label: 'Tasks',
                to: '/console/$orgId/workload-manager/tasks',
            },

            ...(complaintsWorkloadEnabled
                ? [
                      {
                          label: 'Complaints',
                          to: '/console/$orgId/workload-manager/complaints',
                      } satisfies TabItem,
                  ]
                : []),
            ...(emailsEnabled
                ? [
                      isLiteOrg
                          ? ({
                                label: (
                                    <span className="inline-flex items-center">
                                        Emails
                                        <UpgradeTag className="mr-0 ml-2" />
                                    </span>
                                ),
                                to: '/console/$orgId/workload-manager/emails',
                                onNavigate: (_options, e) =>
                                    showLiteUpgradePopover('workload-manager-emails-tab', e?.currentTarget),
                            } satisfies TabItem)
                          : ({
                                label: <EmailsTabLabel count={unreadCount?.count} />,
                                to: '/console/$orgId/workload-manager/emails',
                            } satisfies TabItem),
                  ]
                : []),
        ],
        [unreadCount?.count, complaintsWorkloadEnabled, emailsEnabled, isLiteOrg],
    );

    return (
        <PageController route={Route}>
            <Tabs items={items} />
        </PageController>
    );
}
